import { useEffect, useState, type FC } from 'react'
import { observer } from 'mobx-react-lite'
import { debounce } from 'lodash'
import { Dropdown, SearchElement, type IDropdownProps } from 'shared/ui'
import { SearchTypesEnum } from 'widgets/SearchDropdown'
import { OutboundSourcesDropdownContent } from 'widgets/SearchDropdown/ui/DropdownContent/OutboundSourcesDropdownContent/OutboundSourcesDropdownContent'
import { type SearchDropdownOutboundSourceStore } from 'widgets/SearchDropdown/store/searchDropdownOutboundSourceStore'
import styles from './styles.module.scss'

type OutboundSourcesElementProps = {
  parentsDropdownId?: string[]
  store: SearchDropdownOutboundSourceStore
  onRemove: (type: SearchTypesEnum) => void
  activeType: SearchTypesEnum | null
  canShowChildDropdown: boolean
  dropdownProps?: Partial<IDropdownProps>
}

export const OutboundSourcesElement: FC<OutboundSourcesElementProps> = observer(
  ({ parentsDropdownId, store, onRemove, activeType, canShowChildDropdown, dropdownProps }) => {
    const [show, setShow] = useState(false)
    const { hasSelectedItems, selectedItemsCount, firstSelectedItemKey, disableClosing } = store

    const OutboundSourcesLabel = () => {
      const firstSource = store.itemsList.find((item) => item.key === firstSelectedItemKey)

      if (hasSelectedItems && selectedItemsCount === 1 && firstSource) {
        return (
          <div className={styles.countLabelWrapper}>
            <span className={styles.label}>{firstSource.label}</span>
          </div>
        )
      }

      return (
        <span className={styles.countLabelWrapper}>
          <span className={styles.countLabel}>{selectedItemsCount} sources</span>
        </span>
      )
    }

    const onShow = () => {
      const showDropdown = activeType === SearchTypesEnum.outbound_sources && canShowChildDropdown

      setShow(showDropdown)
    }

    const showDebounce = debounce(onShow, 300)

    useEffect(() => {
      showDebounce()
    }, [canShowChildDropdown, activeType])

    return (
      <Dropdown
        ariaLabel={'OutboundSourcesElement'}
        parentsDropdownId={parentsDropdownId}
        iconButtonProps={{
          size: 'medium',
          fontSize: 20,
          ariaLabel: 'OutboundSourcesElement_trigger',
        }}
        show={show}
        triggerComponent={(open) => (
          <SearchElement
            icon={'send'}
            type='Sources'
            contentDefaultValue='select'
            warningContent={!open && !hasSelectedItems}
            removeCallback={() => {
              onRemove(SearchTypesEnum.outbound_sources)
            }}
            content={hasSelectedItems && <OutboundSourcesLabel />}
            isViewOnly={disableClosing}
          />
        )}
        placement={'bottom-start'}
        sizeTriggerButton={'medium'}
        variantTriggerButton={'icon'}
        customComponent={() => <OutboundSourcesDropdownContent store={store} />}
        {...dropdownProps}
      />
    )
  }
)
